import type { Daypart, InboxLaborRow } from '@coffman-tm/shared';

/** Bundle of editable values for one labor row, collected by the block card. */
export interface LaborEdit {
  total_hours: number;
  rate_cents: number;
  daypart: Daypart;
}

/**
 * Roster rate for a daypart, from the row's embedded day/swing rates.
 * Single source for BOTH the initial edit state and the Shift-select
 * repricing so the two can never disagree. Swing falls back to the day
 * rate when the employee has no swing rate (same fallback billing.ts's
 * laborBaseRateCents applies server-side).
 *
 * SUNDAY PREMIUM replaces the daypart choice entirely when the row carries one
 * — the exact precedence `laborBaseRateCents(emp, daypart, premium)` applies
 * server-side, including winning over the swing rate rather than compounding
 * with it. This mirror has to exist because the block card prices from
 * day/swing rates and the save stamps `confirmed_rate_cents` from what the PM
 * sees: if the card showed $106.43 on a Sunday block, the confirmed pair would
 * freeze there and permanently outrank the premium.
 */
export function rateForDaypart(
  row: Pick<InboxLaborRow, 'day_rate_cents' | 'swing_rate_cents' | 'sunday_premium_rate_cents'>,
  daypart: Daypart,
): number {
  const premium = row.sunday_premium_rate_cents;
  if (typeof premium === 'number' && premium > 0) return premium;
  return daypart === 'swing' ? row.swing_rate_cents ?? row.day_rate_cents : row.day_rate_cents;
}

/**
 * Compute the initial edit state for the table from server-side row data.
 * Hours fall back confirmed → parsed. The daypart initializes from the
 * stored row daypart for confirmed rows, else the employee's primary
 * daypart — and the rate falls back confirmed → the roster rate FOR THAT
 * SAME daypart (the server's default_rate_cents derives from the row's
 * stored daypart, which is 'day' on a fresh parse and therefore wrong for
 * a swing-primary initialization).
 */
export function initialLaborEdits(rows: InboxLaborRow[]): Record<string, LaborEdit> {
  const out: Record<string, LaborEdit> = {};
  for (const r of rows) {
    const daypart = r.confirmed_total_hours !== null ? r.daypart : r.primary_daypart;
    out[r.daily_labor_entry_id] = {
      total_hours: r.confirmed_total_hours ?? r.parsed_total_hours,
      rate_cents: r.confirmed_rate_cents ?? rateForDaypart(r, daypart),
      daypart,
    };
  }
  return out;
}
